import React from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
  Typography,
  Grid,
  IconButton,
  GridItemsAlignment,
} from '@material-ui/core';
import clsx from 'clsx';
import { X, AlertTriangle, Info } from 'react-feather';
import { errorIcon } from '../../../svg';
import { colors } from '../../../theme';

type MessageVariants = 'warning' | 'error' | 'info';
const iconSize = 24;

export type MessageBoxProps = {
  message: React.ReactNode;
  variant: MessageVariants;
  onCloseClick?: () => void;
  hideCloseButton?: boolean;
  flexAlign?: GridItemsAlignment;
  customStyle?: React.CSSProperties;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      padding: theme.spacing(1),
      borderRadius: 10,
    },
    warningContainer: {
      backgroundColor: colors.lightning_yellow,
    },
    errorContainer: {
      backgroundColor: colors.bright_pink,
    },
    infoContainer: {
      backgroundColor: colors.onahau,
    },
    whiteText: {
      fontWeight: 600,
      color: colors.white,
      maxWidth: `calc(100% - ${iconSize + theme.spacing(1)}px)`,
    },
    madisonText: {
      fontWeight: 500,
      color: colors.madison,
      lineHeight: '17px',
      maxWidth: `calc(100% - ${iconSize + theme.spacing(1)}px)`,
    },
    iconContainer: {
      marginRight: theme.spacing(1),
    },
  }),
);

const MessageBox = ({
  message,
  variant,
  onCloseClick,
  hideCloseButton,
  flexAlign = 'center',
  customStyle,
}: MessageBoxProps): JSX.Element => {
  const classes = useStyles(variant);
  if (message === '') {
    return <div />;
  }

  const setVariantClass = (): string => {
    switch (variant) {
      case 'error': {
        return classes.errorContainer;
      }
      case 'warning': {
        return classes.warningContainer;
      }
      default: {
        return classes.infoContainer;
      }
    }
  };

  const renderIcon = (): JSX.Element => {
    switch (variant) {
      case 'error': {
        return (
          <img
            alt="error_icon"
            src={errorIcon}
            style={{ marginRight: '5px' }}
            data-testid="error-icon"
          />
        );
      }
      case 'warning': {
        return (
          <AlertTriangle
            size={iconSize}
            data-testid="warning-icon"
            className={classes.iconContainer}
            color={colors.white}
          />
        );
      }
      default: {
        return (
          <Info
            size={iconSize}
            data-testid="info-icon"
            className={classes.iconContainer}
            color={colors.madison}
          />
        );
      }
    }
  };
  return (
    <div
      data-testid="container"
      className={clsx(classes.container, setVariantClass())}
      style={customStyle}
    >
      <Grid container alignItems="center">
        <Grid
          item
          xs={hideCloseButton ? 12 : 11}
          container
          alignItems={flexAlign}
          data-testid="gridContainerId"
        >
          {renderIcon()}
          <Typography
            variant="caption"
            className={
              variant !== 'info' ? classes.whiteText : classes.madisonText
            }
          >
            {message}
          </Typography>
        </Grid>
        {!hideCloseButton && (
          <Grid
            container
            alignItems="center"
            justifyContent="flex-end"
            item
            xs={1}
          >
            <IconButton data-testid="close-button" onClick={onCloseClick}>
              <X
                size={16}
                color={variant !== 'info' ? colors.white : colors.madison}
              />
            </IconButton>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default MessageBox;
